// Vacaciones urgentes — los dos códigos del circuito, como funciones puras.
//
// Este archivo es el contrato con el sistema de vacaciones y no importa nada:
// así se puede testear en Node y contrastar con los vectores del documento
// docs/plans/2026-09-11-vacaciones-urgente-design.md. Cualquier cambio acá se
// replica en el backend de vacaciones.
//
// - Código de solicitud (lo genera vacaciones, lo lee esta app): LLLLLDDMMV,
//   legajo a 5 dígitos, día, mes y dígito verificador Luhn. No es secreto.
// - Código de habilitación (lo genera esta app, lo valida vacaciones):
//   HMAC-SHA256(secreto, "LLLLL|inicio|hoy") truncado a 6 dígitos. Vale solo el
//   día en que se genera, para ese legajo y esa fecha de inicio.

const LARGO_SOLICITUD = 10;

export function digitoLuhn(digitos) {
  let suma = 0;
  const desdeDerecha = String(digitos).split("").reverse();
  for (let i = 0; i < desdeDerecha.length; i++) {
    let d = parseInt(desdeDerecha[i], 10);
    if (i % 2 === 0) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    suma += d;
  }
  return (10 - (suma % 10)) % 10;
}

export const normalizarSolicitud = (texto) => String(texto ?? "").replace(/\s+/g, "");

/**
 * Formatea la solicitud para leerla fácil: "12345 1510 2".
 */
export const formatearSolicitud = (solicitud) =>
  `${solicitud.slice(0, 5)} ${solicitud.slice(5, 9)} ${solicitud.slice(9)}`;

/**
 * Fecha local en ISO (AAAA-MM-DD). No se usa toISOString porque convierte a
 * UTC y cerca de la medianoche cambia el día.
 */
export function fechaIso(fecha) {
  const a = fecha.getFullYear();
  const m = String(fecha.getMonth() + 1).padStart(2, "0");
  const d = String(fecha.getDate()).padStart(2, "0");
  return `${a}-${m}-${d}`;
}

const soloFecha = (fecha) => new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());

/**
 * El año no viaja en la solicitud: es la primera vez que cae ese día/mes desde
 * hoy. Devuelve null si el día/mes no existe en el año que corresponde.
 */
export function inferirFechaInicio(dia, mes, hoy) {
  const desde = soloFecha(hoy);
  for (const anio of [desde.getFullYear(), desde.getFullYear() + 1]) {
    const candidata = new Date(anio, mes - 1, dia);
    const existe = candidata.getMonth() === mes - 1 && candidata.getDate() === dia;
    if (existe && candidata >= desde) return candidata;
  }
  return null;
}

/**
 * Interpreta un código de solicitud. Devuelve { legajo, fechaInicio } o null si
 * el código no es válido (largo, dígito verificador o fecha).
 */
export function parsearSolicitud(texto, hoy = new Date()) {
  const solicitud = normalizarSolicitud(texto);
  if (!new RegExp(`^\\d{${LARGO_SOLICITUD}}$`).test(solicitud)) return null;

  const cuerpo = solicitud.slice(0, 9);
  if (digitoLuhn(cuerpo) !== parseInt(solicitud[9], 10)) return null;

  const legajo = parseInt(solicitud.slice(0, 5), 10);
  if (legajo < 1) return null;

  const dia = parseInt(solicitud.slice(5, 7), 10);
  const mes = parseInt(solicitud.slice(7, 9), 10);
  const fechaInicio = inferirFechaInicio(dia, mes, hoy);
  if (!fechaInicio) return null;

  return { legajo, fechaInicio };
}

/**
 * Arma un código de solicitud. La app no lo usa en producción (lo genera el
 * sistema de vacaciones), pero sirve para los tests y para probar a mano.
 */
export function armarSolicitud(legajo, dia, mes) {
  const cuerpo =
    String(legajo).padStart(5, "0") + String(dia).padStart(2, "0") + String(mes).padStart(2, "0");
  return cuerpo + digitoLuhn(cuerpo);
}

export const mensajeHabilitacion = (legajo, fechaInicio, hoy) =>
  `${String(legajo).padStart(5, "0")}|${fechaIso(fechaInicio)}|${fechaIso(hoy)}`;

/**
 * Código de habilitación: HMAC-SHA256 del mensaje, primeros 4 bytes como
 * entero sin signo big-endian, módulo 1.000.000, a 6 dígitos.
 */
export async function generarHabilitacion(legajo, fechaInicio, hoy, secreto) {
  const codificador = new TextEncoder();
  const clave = await crypto.subtle.importKey(
    "raw",
    codificador.encode(secreto),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const mac = await crypto.subtle.sign(
    "HMAC",
    clave,
    codificador.encode(mensajeHabilitacion(legajo, fechaInicio, hoy)),
  );
  const n = new DataView(mac).getUint32(0, false);
  return String(n % 1_000_000).padStart(6, "0");
}
